import { useState } from "react";
import { Card, CardContent, CardFooter, Spinner } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useCapabilities } from "@/hooks/useCapabilities";
import { useGlobalStore } from "@/stores/GlobalStore";
import { getDashboardConfig } from "@/config/dashboards";
import { brandIconSrc } from "@/config/tenants";

type DashboardConfigValue = ReturnType<typeof getDashboardConfig>;
type Translate = (key: string) => string;
type MenuItemLabels = Record<string, string | undefined> | undefined;

interface MenuTile {
  name: string;
  title: string;
  img: string | undefined;
  link: string;
  dataID: string;
}

interface MenuTileInputs {
  t: Translate;
  folder: string;
  menuItems: MenuItemLabels;
  dashboard: DashboardConfigValue;
  canAuthorWorkflows: boolean;
  canViewKnowledgeHub: boolean;
  canViewMonitoring: boolean;
}

// Add dashboard as first item if configured to show in sidebar
const buildDashboardTiles = (
  t: Translate,
  folder: string,
  dashboard: DashboardConfigValue,
): MenuTile[] =>
  dashboard?.showInSideBar && dashboard?.url
    ? [
        {
          name: dashboard.title ? t(dashboard.title) : t("Dashboard"),
          title: dashboard.subtitle ? t(dashboard.subtitle) : t("Dashboard"),
          img: brandIconSrc(folder, "dashboard.svg"),
          link: `/${dashboard.urlSegment ?? "dashboard"}`,
          dataID: "dashboard",
        },
      ]
    : [];

const buildChatAndLibraryTiles = (
  t: Translate,
  folder: string,
  menuItems: MenuItemLabels,
): MenuTile[] => [
  {
    name: t(menuItems?.Pathfinder || "Chat"),
    title: t("Chat"),
    img: brandIconSrc(folder, "pathfinder.svg"),
    link: "/chat",
    dataID: "chat",
  },
  {
    name: t(menuItems?.["Library"] || "Library"),
    title: t("library.menuSubtitle"),
    img: brandIconSrc(folder, "library.svg"),
    link: "/library",
    dataID: "library",
  },
];

// Workflow Builder - same `canAuthorWorkflows` capability and same position
// relative to Library as the sidebar entry in `SidebarMenuItems.tsx`, so the
// landing tiles and the sidebar cannot disagree about what a user can reach.
// Ships for every tenant with no `isFolder` gating, per ADR-0021.
const buildWorkflowTiles = (t: Translate, folder: string): MenuTile[] => [
  {
    name: t("workflowBuilder.nav.title"),
    title: t("workflowBuilder.menuSubtitle"),
    img: brandIconSrc(folder, "workflow-builder.svg"),
    link: "/workflow-builder",
    dataID: "workflow-builder",
  },
  // Automated Workflows (LSEG-107) - same capability and same position
  // relative to the Builder as the sidebar entry, so the tiles and the
  // sidebar cannot disagree about what a user can reach. Scheduling a
  // workflow and triggering a run are mutations, so the read flag alone
  // is not enough.
  {
    name: t("automations.nav.title"),
    title: t("automations.menuSubtitle"),
    img: brandIconSrc(folder, "workflow-automation.svg"),
    link: "/automations",
    dataID: "automations",
  },
];

// Knowledge Hub - check secure permission
const buildKnowledgeHubTiles = (
  t: Translate,
  folder: string,
  menuItems: MenuItemLabels,
): MenuTile[] => [
  {
    name: t(menuItems?.["Knowledge Hub"] || "Knowledge Hub"),
    title: t("Secure access to your data & knowledge"),
    img: brandIconSrc(folder, "knowledge.svg"),
    link: "/datahub",
    dataID: "data-hub",
  },
];

// Monitoring - check secure permission (uses solutions.svg icon)
const buildMonitoringTiles = (
  t: Translate,
  folder: string,
  menuItems: MenuItemLabels,
): MenuTile[] => [
  {
    name: t(menuItems?.["Monitoring"] ?? "Monitoring"),
    title: t("Monitor AI usage and performance"),
    img: brandIconSrc(folder, "solutions.svg"),
    link: "/metrics",
    dataID: "monitoring",
  },
];

const buildClosingTiles = (t: Translate, folder: string, menuItems: MenuItemLabels): MenuTile[] => [
  // Models Overview - always available (no permission required)
  {
    name: t("Models Overview"),
    title: t("View available AI models and capabilities"),
    img: brandIconSrc(folder, "models.svg"),
    link: "/models",
    dataID: "models-overview",
  },
  // Settings - available to all authenticated users
  {
    name: t(menuItems?.["Settings"] ?? "Settings"),
    title: t("Configure your AI platform"),
    img: brandIconSrc(folder, "settings.svg"),
    link: "/settings",
    dataID: "settings",
  },
];

/** Build the tile list from the shared capability layer. */
const buildMenuTiles = (inputs: MenuTileInputs): MenuTile[] => {
  const { t, folder, menuItems, dashboard } = inputs;

  return [
    ...buildDashboardTiles(t, folder, dashboard),
    ...buildChatAndLibraryTiles(t, folder, menuItems),
    ...(inputs.canAuthorWorkflows ? buildWorkflowTiles(t, folder) : []),
    ...(inputs.canViewKnowledgeHub ? buildKnowledgeHubTiles(t, folder, menuItems) : []),
    ...(inputs.canViewMonitoring ? buildMonitoringTiles(t, folder, menuItems) : []),
    ...buildClosingTiles(t, folder, menuItems),
  ];
};

const MenuCardPending: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-3/5">
      <div aria-live="polite" className="flex items-center gap-3 text-default-500" role="status">
        <Spinner size="sm" />
        {t("Loading permissions...")}
      </div>
    </div>
  );
};

interface MenuTileCardProps {
  item: MenuTile;
  iconSize: number | undefined;
  isCompact: boolean;
  isImageFailed: boolean;
  onNavigate: (link: string) => void;
  onImageError: (dataID: string) => void;
}

const MenuTileCard: React.FC<MenuTileCardProps> = ({
  item,
  iconSize,
  isCompact,
  isImageFailed,
  onNavigate,
  onImageError,
}) => (
  <Card
    className="py-4 cursor-pointer transition-[transform,box-shadow] duration-200 hover:shadow-lg hover:scale-102 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
    data-id={item.dataID}
    role="button"
    tabIndex={0}
    onClick={() => onNavigate(item.link)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onNavigate(item.link);
      }
    }}
  >
    <CardContent className="flex justify-center items-center overflow-hidden">
      <div className="w-full flex flex-col items-center justify-center gap-2 py-4">
        {item.img && !isImageFailed && (
          <img
            alt=""
            src={item.img}
            style={{
              borderRadius: 12,
              height: iconSize || 64,
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
            }}
            onError={() => onImageError(item.dataID)}
          />
        )}
        <div
          className={`text-center text-foreground font-bold ${!isCompact ? "text-sm" : "text-xl"}`}
        >
          {item.name}
        </div>
      </div>
    </CardContent>
    {isCompact && (
      <CardFooter className="px-4 pb-4 pt-0 justify-center">
        <small className="text-default-600 text-center">{item.title}</small>
      </CardFooter>
    )}
  </Card>
);

export default function MenuCard() {
  const navigate = useNavigate();
  const { canAuthorWorkflows, canViewKnowledgeHub, canViewMonitoring, isSettled } =
    useCapabilities();
  const { brand } = useBrand();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact } = sidebar;
  const { t } = useTranslation();

  // Tenant packs do not all ship every menu icon, so a tile whose image 404s
  // drops the image and renders label-only rather than showing the browser's
  // broken-image glyph. Same approach as Sidebar's `failedImages` set.
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const markImageFailed = (dataID: string) =>
    setFailedImages((previous) => {
      const next = new Set(previous);

      next.add(dataID);

      return next;
    });

  const list = buildMenuTiles({
    t,
    folder: brand.folder,
    menuItems: brand.copy.menuItems,
    dashboard: getDashboardConfig(brand.folder),
    canAuthorWorkflows,
    canViewKnowledgeHub,
    canViewMonitoring,
  });

  // Show loading state until the RBAC grants have settled, so the Workflow
  // Builder tile is not briefly absent for a user who is entitled to it. This
  // now paints on most visits rather than being effectively dead code, so it
  // follows the Library page's pending convention — a spinner beside the label,
  // announced politely — instead of a bare line of text.
  if (!isSettled) return <MenuCardPending />;

  return (
    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-3/5">
      {list.map((item) => (
        // `dataID` is the stable identifier for a tile. `title` is a translated
        // subtitle, so keying on it would remount cards on a language change and
        // silently drop one if two locales happen to translate to the same string.
        <MenuTileCard
          key={item.dataID}
          iconSize={brand.layout.menuItemsIconSize}
          isCompact={isSidebarCompact}
          isImageFailed={failedImages.has(item.dataID)}
          item={item}
          onImageError={markImageFailed}
          onNavigate={navigate}
        />
      ))}
    </div>
  );
}
